import { c as _c } from "react/compiler-runtime";
import type { TempMedia } from '@/payload-types';
import { DialogTitle } from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, Info, Loader2 } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import Markdown from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import MermaidRenderer from '@/app/(app)/(menu)/mermaid/MermaidRenderer';
import { useLocalization } from '@/app/(app)/_providers/localization/LocalizationProvider';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog';
import { getTempMediaByIds } from '../actionsImage';
import { getExtension, isImage } from './tempMediaUtils';
type ChatBubbleMessage = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: string[];
  tempMediaIds?: number[];
};
function MessageTempMediaDisplay(t0) {
  const $ = _c(13);
  const {
    tempMediaIds
  } = t0;
  let t1;
  let t2;
  if ($[0] !== tempMediaIds) {
    t1 = ["tempMedia", "byIds", tempMediaIds];
    t2 = () => getTempMediaByIds(tempMediaIds);
    $[0] = tempMediaIds;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  const t3 = tempMediaIds.length > 0;
  let t4;
  if ($[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: t5
  } = useQuery(t4);
  let t6;
  if ($[7] !== t5) {
    t6 = t5 === undefined ? [] : t5;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const tempMedia = t6;
  if (tempMediaIds.length === 0 || tempMedia.length === 0) {
    return null;
  }
  let t7;
  if ($[9] !== tempMedia) {
    t7 = tempMedia.map(_temp);
    $[9] = tempMedia;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = <div className="flex flex-wrap gap-2">{t7}</div>;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  return t8;
}
function _temp(media, index) {
  return <div key={media.id} className="flex flex-col">{isImage(media) && <Image src={media.url || ""} alt={media.filename || "attached image"} width={1080} height={1080} className="aspect-video max-w-80 rounded-md object-contain" />}{!isImage(media) && <span className="text-muted-foreground text-xs">file{index + 1}{getExtension(media)}</span>}</div>;
}
export function ChatBubble(t0) {
  const $ = _c(26);
  const {
    message,
    isStreaming: t1
  } = t0;
  const isStreaming = t1 === undefined ? false : t1;
  const {
    messages
  } = useLocalization();
  const isAssistant = message.role === "assistant";
  let t2;
  if ($[0] !== message.content) {
    t2 = message.content.includes("```mermaid");
    $[0] = message.content;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const hasMermaidFenceStart = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = /```mermaid[\s\S]*?```/;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== message.content) {
    t4 = t3.test(message.content);
    $[3] = message.content;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const hasCompleteMermaidFence = t4;
  const showDiagramPlaceholder = isAssistant && isStreaming && hasMermaidFenceStart && !hasCompleteMermaidFence;
  let t5;
  if ($[5] !== message.content || $[6] !== showDiagramPlaceholder) {
    const contentForRender = showDiagramPlaceholder ? message.content.replace(/```mermaid[\s\S]*$/, "").trimEnd() : message.content;
    const chunks = [];
    const regex = /```mermaid[\s\S]*?```/g;
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(contentForRender)) !== null) {
      if (match.index > lastIndex) {
        chunks.push({
          type: "text",
          value: contentForRender.slice(lastIndex, match.index)
        });
      }
      chunks.push({
        type: "mermaid",
        value: match[0]
      });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < contentForRender.length) {
      chunks.push({
        type: "text",
        value: contentForRender.slice(lastIndex)
      });
    }
    t5 = chunks.filter(_temp2);
    $[5] = message.content;
    $[6] = showDiagramPlaceholder;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const parts = t5;
  if (!isAssistant) {
    let t6;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = {
        maxWidth: "80%"
      };
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== message.tempMediaIds) {
      t7 = message.tempMediaIds && message.tempMediaIds.length > 0 && <MessageTempMediaDisplay tempMediaIds={message.tempMediaIds} />;
      $[9] = message.tempMediaIds;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== message.content || $[12] !== t7) {
      t8 = <div className="flex w-full justify-end"><div className="border-border bg-muted text-foreground rounded-4xl border px-4.5 py-2.5 text-sm leading-snug" style={t6}>{message.content}{t7}</div></div>;
      $[11] = message.content;
      $[12] = t7;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    return t8;
  }
  let t6;
  if ($[14] !== message.id || $[15] !== parts) {
    t6 = parts.map((part, index) => part.type === "mermaid" ? <div key={`${message.id}-mermaid-${index}`}><Dialog><DialogTrigger asChild={true}><Button variant="ghost" size="auto" className="w-full"><MermaidRenderer source={part.value} /></Button></DialogTrigger><DialogContent className="flex h-[calc(100vh-2rem)] max-h-none w-[calc(100vw-2rem)] max-w-none flex-col overflow-hidden p-4 sm:max-w-none"><DialogTitle className="sr-only">Mermaid Diagram</DialogTitle><MermaidRenderer source={part.value} className="flex min-h-0 w-full flex-1 items-center justify-center [&_svg]:h-auto [&_svg]:max-h-full [&_svg]:w-auto [&_svg]:max-w-full" /></DialogContent></Dialog></div> : <div key={`${message.id}-text-${index}`} className="prose dark:prose-invert prose-headings:text-[17px] prose-headings:font-bold prose-headings:mt-5 prose-headings:mb-1.5 prose-p:mb-2.5 prose-p:text-foreground prose-pre:bg-secondary prose-pre:text-foreground prose-pre:border prose-pre:border-border prose-li:mb-1.5 prose-li:leading-relaxed prose-li:text-foreground prose-strong:font-semibold prose-strong:text-foreground prose-code:rounded prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:text-[13px] prose-code:font-mono prose-code:text-foreground prose-td:text-foreground prose-th:text-foreground max-w-none"><Markdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>{part.value}</Markdown></div>);
    $[14] = message.id;
    $[15] = parts;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== showDiagramPlaceholder) {
    t7 = showDiagramPlaceholder && <div className="border-border bg-card flex w-full max-w-4xl items-center gap-3 rounded-2xl border px-3.5 py-3"><div className="bg-muted text-muted-foreground flex h-8 w-8 shrink-0 items-center justify-center rounded-full"><Loader2 className="h-4 w-4 animate-spin" /></div><div className="min-w-0 flex-1"><p className="text-foreground text-sm font-semibold">Generating diagram...</p><p className="text-muted-foreground text-xs">Building layout and connections</p></div></div>;
    $[17] = showDiagramPlaceholder;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== message.sources || $[20] !== messages) {
    t8 = message.sources && <div className="flex flex-row flex-wrap gap-2 pl-1">{message.sources.map((source, index_0) => <Button key={index_0} asChild={true} variant="outline"><Link href={source} target="_blank" className="flex flex-row items-center gap-2 hover:underline"><Info className="h-4 w-4" /> {messages.chat.source}{" "}{index_0 + 1}{" "}<ExternalLink className="h-4 w-4" /></Link></Button>)}</div>;
    $[19] = message.sources;
    $[20] = messages;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  let t9;
  if ($[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
    t9 = <div className="text-foreground text-[15px] leading-[1.7]"><div className="flex flex-col gap-3">{t6}{t7}{t8}</div></div>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
function _temp2(chunk) {
  return chunk.value.trim().length > 0;
}